<!-- Energy widget version: 1.0 -->
<script lang="ts">
	import { format } from 'svelte-i18n';
	import { Container, Loader, ProgressBar, Text } from '@Shelly/shelly-design-system';
	import { user_settings } from 'src/stores';
	import EmInfoTooltip from 'src/components/EnergyMonitoring/EmInfoTooltip.svelte';
	import { fetchEnergyStatistics } from 'src/components/EnergyMonitoring/helpers/fetch_energy_statistics';
	import {
		aggregateTariffBuckets,
		buildTariffSummary,
	} from 'src/components/EnergyMonitoring/helpers/energy_tariff_helpers';
	import { buildPeriodSummaryPair } from 'src/components/EnergyMonitoring/helpers/energy_kpi_helpers';
	import { isEnergyLivePeriod } from 'src/components/EnergyMonitoring/helpers/period_helpers';
	import { getControlsForEnergyConfig } from '../../../../dash-energy-period';
	import { normalizeEnergyConfig } from '../../../../dash-energy-helpers';
	import type { DashEnergyWidgetConfig } from '../../../../dash-energy-types';
	import type { ChartStatisticInterface } from 'src/types/Chart';
	import type { TariffSummary } from 'src/components/EnergyMonitoring/helpers/energy_tariff_helpers';
	import { energyMonitoringDataMode } from 'src/components/EnergyMonitoring/helpers/store';
	import { buildDemoEnergyMonitoringStats } from 'src/components/EnergyMonitoring/helpers/energy_demo_providers';
	import { currentUnitId } from 'src/stores/account/home';

	export let energyConfig: DashEnergyWidgetConfig<'dashEnergyKpiPeriod'> | undefined;
	export let staticPreview = false;

	$: config = normalizeEnergyConfig('dashEnergyKpiPeriod', energyConfig);

	let loading = true;
	let statistic: ChartStatisticInterface | null = null;
	let tariffSummary: TariffSummary | null = null;
	let loadToken = 0;

	const loadData = async (
		config: DashEnergyWidgetConfig<'dashEnergyKpiPeriod'>,
		unitId: string,
		dataMode: 'real' | 'demo',
		staticPreview: boolean,
	) => {
		void unitId;
		const token = ++loadToken;
		loading = true;
		const normalized = normalizeEnergyConfig('dashEnergyKpiPeriod', config);
		if (staticPreview || dataMode === 'demo') {
			const demo = buildDemoEnergyMonitoringStats();
			statistic = demo.statistic;
			tariffSummary = buildTariffSummary(demo.tariffBuckets);
			loading = false;
			return;
		}
		try {
			const controls = getControlsForEnergyConfig(normalized);
			const result = await fetchEnergyStatistics(controls, normalized.period);
			if (token !== loadToken) return;
			statistic = result?.statistic ?? null;
			tariffSummary = buildTariffSummary(aggregateTariffBuckets(result?.history ?? []));
		} catch {
			if (token !== loadToken) return;
			statistic = null;
			tariffSummary = null;
		} finally {
			if (token === loadToken) loading = false;
		}
	};

	$: loadData(config, $currentUnitId, $energyMonitoringDataMode, staticPreview);

	$: pair = buildPeriodSummaryPair(
		statistic,
		tariffSummary,
		config.period,
		$user_settings?.currency ?? '',
	);
	$: showTariffBar = config.showTariffBar !== false && pair.tariffMode !== 'none';
	$: periodLabel = $format(
		isEnergyLivePeriod(config.period)
			? 'energy_monitoring_kpi_tariff'
			: 'energy_monitoring_period_consumption',
		{
			default: isEnergyLivePeriod(config.period) ? 'Today' : 'Period consumption',
		},
	);
</script>

<Container type="glass" noScroll fullHeight>
	{#if loading}
		<div class="dash-energy-kpi-period-loading">
			<Loader size="m" />
		</div>
	{:else}
		<div class="dash-energy-kpi-period">
			<div class="dash-energy-kpi-values">
				<div class="dash-energy-kpi-value-block">
					<Text styleType="miniature" class="text-color-muted">{periodLabel}</Text>
					<Text styleType="h2" class="text-600">{pair.consumptionValue}</Text>
				</div>
				<div class="dash-energy-kpi-value-block dash-energy-kpi-value-right">
					<Text styleType="miniature" class="text-color-muted">
						{$format('energy_monitoring_period_cost', { default: 'Reported cost' })}
					</Text>
					<div class="dash-energy-kpi-value-heading">
						<Text styleType="h2" class="text-600">{pair.costValue}</Text>
						<EmInfoTooltip
							text={$format('energy_monitoring_kpi_tariff_info', {
								default: 'Electricity cost reported for the selected period.',
							})}
						/>
					</div>
				</div>
			</div>
			{#if showTariffBar}
				<div class="dash-energy-kpi-bar">
					<Text styleType="miniature" class="text-color-muted">
						{pair.tariffMode === 'equal'
							? $format('energy_monitoring_equal_rate_schedule', {
									default: 'Equal-rate schedule',
								})
							: $format('energy_monitoring_off_peak_used', { default: 'Off-peak used' })}
						{pair.tariffCaption !== '—' ? ` · ${pair.tariffCaption}` : ''}
					</Text>
					{#if pair.tariffMode === 'tiered'}
						<ProgressBar progress={pair.tariffProgress} type={pair.tariffProgressType} />
					{/if}
				</div>
			{/if}
		</div>
	{/if}
</Container>

<style lang="scss">
	.dash-energy-kpi-period,
	.dash-energy-kpi-period-loading {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-m);
		min-width: 0;
		padding: var(--spacing-s);
	}

	.dash-energy-kpi-period-loading {
		align-items: center;
		justify-content: center;
		min-height: 120px;
	}

	.dash-energy-kpi-values {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: var(--spacing-m);
	}

	.dash-energy-kpi-value-block {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xxs);
		min-width: 0;
	}

	.dash-energy-kpi-value-right {
		align-items: flex-end;
		text-align: right;
	}

	.dash-energy-kpi-value-heading {
		display: inline-flex;
		align-items: center;
		gap: var(--spacing-xxs);
	}

	.dash-energy-kpi-bar {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xs);
	}
</style>
